import {
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    model,
    ModelSignal,
    Signal
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Country } from '@shs-digi-care-monorepo/interfaces';
import '@shui/core/sh-tooltip';
import { SelectedCountryService } from '../../services/selected-country.service';

/**
 * Narrow rail left of the main navigation that switches the application between
 * the countries the user owns tasks in, plus a globe entry that puts all of
 * them in scope at once. Rendered only for task owners with more than one
 * assignment, since there is otherwise nothing to switch between. It also owns
 * the collapse toggle of the sidebar and slides out while the sidebar is collapsed.
 */
@Component({
    selector: 'app-country-switch',
    templateUrl: './country-switch.component.html',
    // The menu items are square tiles holding nothing but a flag, so the default
    // label-driven paddings and the offset of the functions slot are reset.
    styles: `
        sh-menu-item {
            width: 2.5rem;
            height: 2.5rem;
            flex-shrink: 0;
            --sh-menu-item-item-height: 2.5rem;
            --sh-menu-item-item-width: 100%;
            --sh-menu-item-item-padding: 0;
            --sh-menu-item-item-padding-hover: 0;
            --sh-menu-item-item-margin-hover: 0;
            --sh-menu-item-functions-slot-margin: 0;
            --sh-menu-item-icon-size: 2.5rem;
        }

        .country-list {
            scrollbar-width: thin;
            scrollbar-color: var(--color-text-disabled) transparent;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: {
        '[style.flex]': '"0 0 auto"',
        '[style.overflow]': '"hidden"',
        '[style.width]': 'expanded() ? expandedWidth : "0px"',
        '[style.opacity]': 'expanded() ? 1 : 0',
        '[style.transition]': '"width 200ms ease-in-out, opacity 200ms ease-in-out"',
        '[attr.inert]': 'expanded() ? null : ""'
    }
})
export class CountrySwitchComponent {
    private readonly countryScope = inject(SelectedCountryService);

    /** Width of the rail including its gap to the sidebar. */
    protected readonly expandedWidth = '4.5rem';

    /** Collapsed state of the sidebar, the rail is only shown while the sidebar is expanded. */
    public readonly collapsed: ModelSignal<boolean> = model(false);

    protected readonly countries: Signal<Country[]> = this.countryScope.countries;
    protected readonly selectedCountry: Signal<Country | null> = this.countryScope.selected;
    protected readonly canSwitch: Signal<boolean> = this.countryScope.canSwitch;
    protected readonly expanded: Signal<boolean> = computed(() => this.canSwitch() && !this.collapsed());

    /** Collapses the sidebar, which also hides the rail. */
    protected collapse(): void {
        this.collapsed.set(true);
    }

    /** Scopes the application to one country, or to all of them with `null`. */
    protected selectCountry(country: Country | null): void {
        this.countryScope.select(country);
    }
}
